import { useState, useEffect, useMemo, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { useApi } from '../../hooks/useApi';
import { Th } from '../../utils/api';
import { ChartCard, ChartLoader, ChartError, LineChart, StackedBar } from '../../components/Charts';
import { FilterSelect, useThFilters, appColor } from './thShared';
import { fmtNum } from '../../utils/theme';

// Thailand province GeoJSON — loaded once from local static file and cached
const MAP_NAME = 'ThailandProvinces';

// DB stores province names all-lowercase ("chiang mai"); GeoJSON uses Title Case ("Chiang Mai")
const titleCase = s => s.replace(/\b\w/g, c => c.toUpperCase());
let _geoState = 'idle'; // 'idle' | 'loading' | 'ready' | 'error'
const _geoWaiters = [];

function loadGeoJson(onDone) {
  if (_geoState === 'ready')  { onDone(true);  return; }
  if (_geoState === 'error')  { onDone(false); return; }
  _geoWaiters.push(onDone);
  if (_geoState === 'loading') return;
  _geoState = 'loading';
  fetch('/maps/thailand.json')
    .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
    .then(geo => {
      echarts.registerMap(MAP_NAME, geo);
      _geoState = 'ready';
      _geoWaiters.forEach(fn => fn(true));
      _geoWaiters.length = 0;
    })
    .catch(err => {
      console.error('Thailand GeoJSON load failed:', err);
      _geoState = 'error';
      _geoWaiters.forEach(fn => fn(false));
      _geoWaiters.length = 0;
    });
}

export default function ThWeeklyGeoView({ vertical = 'ridehailing' }) {
  const { filters, filtersLoaded, category, setCategory } = useThFilters(vertical);

  const [metric,           setMetric]           = useState('unique_visitors');
  const [selectedWeek,     setSelectedWeek]     = useState('');
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [mapReady,         setMapReady]         = useState(_geoState === 'ready');
  const [geoError,         setGeoError]         = useState(_geoState === 'error');

  // Load GeoJSON once
  useEffect(() => {
    loadGeoJson(ok => {
      setMapReady(ok);
      setGeoError(!ok);
    });
  }, []);

  // Default week = most recent
  useEffect(() => {
    if (filters.weeks.length) setSelectedWeek(w => w || filters.weeks[0]);
  }, [filters.weeks]);

  // Fetch all province-level weekly data
  const params = useMemo(() => ({ vertical, category }), [vertical, category]);

  const { data, loading, error } = useApi(
    () => Th.weeklyGeo(vertical, { category }),
    params,
    filtersLoaded,
  );

  // Last 6 weeks present in data
  const weeks = useMemo(
    () => [...new Set((data || []).map(r => r.week))].sort().slice(-6),
    [data],
  );

  // Sync selectedWeek to available weeks
  useEffect(() => {
    if (weeks.length && !weeks.includes(selectedWeek))
      setSelectedWeek(weeks[weeks.length - 1]);
  }, [weeks, selectedWeek]);

  // Province totals for the selected week
  const weekRows = useMemo(
    () => (data || []).filter(r => r.week === selectedWeek),
    [data, selectedWeek],
  );

  const maxValue = useMemo(
    () => Math.max(1, ...weekRows.map(r => r[metric] || 0)),
    [weekRows, metric],
  );

  const mapData = useMemo(
    () => weekRows
      .filter(r => r.province !== 'UNKNOWN')
      .map(r => ({ name: titleCase(r.province), value: r[metric] || 0 })),
    [weekRows, metric],
  );

  // Weekly trend for clicked province — compare title-cased DB name to selectedProvince
  const provinceTrend = useMemo(() => {
    if (!selectedProvince) return [];
    return weeks.map(w => {
      const row = (data || []).find(
        r => r.week === w && titleCase(r.province) === selectedProvince
      );
      return row ? row[metric] : null;
    });
  }, [data, selectedProvince, weeks, metric]);

  // Per-app weekly data for the selected province (for market share stacked bar)
  const provinceAppParams = useMemo(() => ({
    vertical, category,
    province: selectedProvince ? selectedProvince.toLowerCase() : 'ALL',
  }), [vertical, category, selectedProvince]);

  const { data: appData, loading: appLoading } = useApi(
    () => Th.weekly(vertical, { province: selectedProvince ? selectedProvince.toLowerCase() : 'ALL', category }),
    provinceAppParams,
    !!selectedProvince && filtersLoaded,
  );

  // Market share series by app for the province (last 6 weeks)
  const { appSeries, appWeeks } = useMemo(() => {
    if (!appData || !appData.length) return { appSeries: [], appWeeks: [] };
    const aw = [...new Set(appData.map(r => r.week))].sort().slice(-6);
    const apps = [...new Set(appData.map(r => r.app_name))].sort();
    const series = apps.map((app, i) => {
      const byWeek = Object.fromEntries(
        appData.filter(r => r.app_name === app).map(r => [r.week, r[metric]])
      );
      return { name: app, color: appColor(app, i), data: aw.map(w => byWeek[w] ?? null) };
    });
    return { appSeries: series, appWeeks: aw };
  }, [appData, metric]);

  // Province market share % of national total per week
  const provinceShareTrend = useMemo(() => {
    if (!selectedProvince) return [];
    return weeks.map((w, i) => {
      const pVal = provinceTrend[i];
      if (pVal == null) return null;
      const natTotal = (data || [])
        .filter(r => r.week === w && r.province !== 'UNKNOWN')
        .reduce((s, r) => s + (r[metric] || 0), 0);
      return natTotal > 0 ? +((pVal / natTotal) * 100).toFixed(2) : null;
    });
  }, [data, selectedProvince, weeks, metric, provinceTrend]);

  const metricLabel = metric === 'unique_visitors' ? 'Visitors' : 'Sessions';

  // ECharts map option — only build when map is registered and data is available
  const mapOption = useMemo(() => {
    if (!mapReady) return null;
    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        formatter: p => `<b>${p.name}</b><br/>${metricLabel}: <b>${fmtNum(p.value || 0)}</b>`,
        backgroundColor: '#fff',
        borderColor: '#E5E7EB',
        borderWidth: 1,
        textStyle: { color: '#111827', fontSize: 12 },
        extraCssText: 'box-shadow:0 4px 12px rgba(0,0,0,0.08);border-radius:8px;',
      },
      visualMap: {
        min: 0,
        max: maxValue,
        text: ['High', 'Low'],
        realtime: false,
        calculable: true,
        inRange: { color: ['#E1F5EE', '#5DCAA5', '#1DB89A', '#0F6E56'] },
        textStyle: { color: '#6B7280', fontSize: 10 },
        right: 8,
        top: 'middle',
        itemWidth: 12,
        itemHeight: 100,
      },
      series: [{
        type: 'map',
        map: MAP_NAME,
        roam: true,
        data: mapData,
        emphasis: {
          label: { show: true, fontSize: 10, color: '#fff' },
          itemStyle: { areaColor: '#f59e0b' },
        },
        select: {
          label: { show: true, fontSize: 10, color: '#fff' },
          itemStyle: { areaColor: '#f59e0b' },
        },
        label: { show: false },
        itemStyle: { borderColor: '#fff', borderWidth: 0.5 },
      }],
    };
  }, [mapReady, mapData, maxValue, metricLabel]);

  const onMapClick = useCallback(p => {
    if (p.name) setSelectedProvince(prev => prev === p.name ? null : p.name);
  }, []);

  return (
    <div>
      {/* Filter bar */}
      <div className="filters-bar" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <FilterSelect label="Category" value={category} onChange={setCategory} options={filters.categories} />

        {/* Week selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="filter-label">Week</span>
          <select
            className="filter-select"
            value={selectedWeek}
            onChange={e => setSelectedWeek(e.target.value)}
            style={{ minWidth: 100 }}
          >
            {weeks.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
        </div>

        {/* Visitors / Sessions toggle */}
        <div style={{
          display: 'flex',
          background: 'var(--bg-base, #f3f4f6)',
          border: '1px solid var(--border, #e5e7eb)',
          borderRadius: 8, padding: 2,
        }}>
          {[
            { key: 'unique_visitors', label: 'Visitors' },
            { key: 'total_sessions',  label: 'Sessions' },
          ].map(m => (
            <button
              key={m.key}
              onClick={() => setMetric(m.key)}
              style={{
                fontSize: 12.5, fontWeight: 600, padding: '5px 16px', borderRadius: 6,
                border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                background: metric === m.key ? '#1DB89A' : 'transparent',
                color:      metric === m.key ? '#fff'    : 'var(--text-muted, #6B7280)',
              }}
            >
              {m.label}
            </button>
          ))}
        </div>

        {selectedProvince && (
          <button
            onClick={() => setSelectedProvince(null)}
            style={{
              fontSize: 12, fontWeight: 600, color: '#EF4444',
              background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.20)',
              borderRadius: 100, padding: '4px 12px', cursor: 'pointer',
            }}
          >
            ✕ {selectedProvince}
          </button>
        )}
      </div>

      {/* Map + drill-down */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: selectedProvince ? '1fr 1fr' : '1fr',
        gap: 16,
        alignItems: 'start',
      }}>

        {/* Choropleth */}
        <ChartCard
          title={`${metricLabel} by Province`}
          sub={`${category} · ${selectedWeek || '—'} · click a province to drill down`}
        >
          {/* GeoJSON load error */}
          {geoError && <ChartError message="Failed to load Thailand map data" height={520} />}

          {/* Loading: waiting for map or data */}
          {!geoError && (loading || !mapReady) && <ChartLoader height={520} />}

          {/* API error (map loaded OK) */}
          {!geoError && mapReady && !loading && error && <ChartError message={error} height={520} />}

          {/* Map rendered — key forces remount (resize) when layout switches between 1-col and 2-col */}
          {!geoError && mapReady && !loading && !error && mapOption && (
            mapData.length > 0 ? (
              <ReactECharts
                key={selectedProvince ? 'split' : 'full'}
                echarts={echarts}
                option={mapOption}
                style={{ height: 520 }}
                onEvents={{ click: onMapClick }}
                notMerge
              />
            ) : (
              <div className="empty-state" style={{ height: 520, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                No province data for selected filters
              </div>
            )
          )}
        </ChartCard>

        {/* Province drill-down: weekly bar chart */}
        {selectedProvince && (
          <ChartCard
            title={selectedProvince}
            sub={`Weekly ${metricLabel.toLowerCase()} · last 6 weeks`}
          >
            {provinceTrend.some(v => v != null) ? (
              <>
                {/* Province % of national total — line chart */}
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted, #9CA3AF)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                  National Share %
                </div>
                <LineChart
                  categories={weeks}
                  series={[{ name: 'Share %', color: '#1DB89A', data: provinceShareTrend }]}
                  height={200}
                  pointLabels={v => v != null ? `${v}%` : ''}
                />

                {/* App market share within this province — stacked bar */}
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted, #9CA3AF)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 20, marginBottom: 4 }}>
                  App Market Share in {selectedProvince}
                </div>
                {appLoading && <ChartLoader height={220} />}
                {!appLoading && appSeries.length > 0 && (
                  <StackedBar
                    categories={appWeeks}
                    series={appSeries}
                    height={220}
                    normalize
                  />
                )}
                {!appLoading && appSeries.length === 0 && (
                  <div className="empty-state" style={{ height: 80 }}>No app data for {selectedProvince}</div>
                )}
              </>
            ) : (
              <div className="empty-state">No data for {selectedProvince}</div>
            )}
          </ChartCard>
        )}
      </div>
    </div>
  );
}
